import c from 'classnames'
import { es } from 'date-fns/locale'
import React, { useMemo } from 'react'
import { DayPicker, getDefaultClassNames } from 'react-day-picker'
import 'react-day-picker/style.css'
import styles from './Calendar.module.css'

export interface ICalendarProps {
  className?: string
  multipleDateSelection?: boolean
  theme?: any
  selected?: Date | Date[] | null
  onSelect?: (value: Date) => void
  minDate?: Date
  maxDate?: Date
  disabledDays?: (0 | 1 | 2 | 3 | 4 | 5 | 6)[]
  disabledDates?: Date[]
  onScroll?: () => void
  highlightedDates?: Date[]
}

const Calendar = ({
  className,
  multipleDateSelection = false,
  selected,
  onSelect,
  minDate,
  maxDate,
  disabledDays = [],
  disabledDates = [],
  onScroll,
  highlightedDates = [],
}: ICalendarProps) => {
  // Convert disabledDays (0-6 where 0=Sunday) to date-fns day of week format
  const disabledDaysOfWeek = useMemo(() => {
    return disabledDays.map((day) => ({ dayOfWeek: [day] }))
  }, [disabledDays])

  // Build disabled matcher
  const disabled = useMemo(() => {
    const matchers: any[] = []

    // Add disabled days of week
    disabledDaysOfWeek.forEach((matcher) => matchers.push(matcher))

    // Add specific disabled dates
    if (disabledDates.length > 0) {
      matchers.push(...disabledDates)
    }

    // Add dates before minDate
    if (minDate) {
      matchers.push({ before: minDate })
    }

    // Add dates after maxDate
    if (maxDate) {
      matchers.push({ after: maxDate })
    }

    return matchers
  }, [disabledDaysOfWeek, disabledDates, minDate, maxDate])

  // Modifiers for highlighted dates
  const modifiers = useMemo(() => {
    const mods: Record<string, Date[]> = {}
    if (highlightedDates.length > 0) {
      mods.highlighted = highlightedDates
    }
    return mods
  }, [highlightedDates])

  // Default month to show
  const defaultMonth = useMemo(() => {
    if (selected) {
      if (Array.isArray(selected) && selected.length > 0) {
        return selected[0]
      }
      if (!Array.isArray(selected)) {
        return selected
      }
    }
    return minDate || new Date()
  }, [selected, minDate])

  const defaultClassNames = getDefaultClassNames()

  const classNames = {
    month_caption: `${styles.monthCaption} ${defaultClassNames.month_caption}`,
    button_previous: `${styles.navButton} ${defaultClassNames.button_previous}`,
    button_next: `${styles.navButton} ${defaultClassNames.button_next}`,
    weekdays: `${styles.weekdays} ${defaultClassNames.weekdays}`,
    weekday: `${styles.weekday} ${defaultClassNames.weekday}`,
    day: `${styles.day} ${defaultClassNames.day}`,
    day_button: `${styles.dayButton} ${defaultClassNames.day_button}`,
    selected: `${styles.selected} ${defaultClassNames.selected}`,
    today: `${styles.today} ${defaultClassNames.today}`,
    outside: `${styles.outside} ${defaultClassNames.outside}`,
    disabled: `${styles.disabled} ${defaultClassNames.disabled}`,
    range_middle: `${styles.rangeMiddle} ${defaultClassNames.range_middle}`,
    range_start: `${styles.rangeStart} ${defaultClassNames.range_start}`,
    range_end: `${styles.rangeEnd} ${defaultClassNames.range_end}`,
  }

  const commonProps = {
    locale: es,
    disabled,
    defaultMonth,
    modifiers,
    modifiersClassNames: {
      highlighted: styles.highlighted,
    },
    showOutsideDays: false,
    fixedWeeks: true,
    weekStartsOn: 1 as const,
    classNames,
  }

  if (multipleDateSelection) {
    // Multiple selection mode
    const selectedArray = Array.isArray(selected) ? selected : selected ? [selected] : []

    const handleMultiSelect = (dates: Date[] | undefined) => {
      if (dates && dates.length > 0 && onSelect) {
        // Find the newly added date (last one)
        const newDate = dates[dates.length - 1]
        onSelect(newDate)
      }
    }

    return (
      <div className={c(styles.calendar, className)} onScroll={onScroll}>
        <DayPicker mode="multiple" selected={selectedArray} onSelect={handleMultiSelect} {...commonProps} />
      </div>
    )
  }

  // Single selection mode
  const selectedDate = Array.isArray(selected) ? selected[0] : selected || undefined

  const handleSingleSelect = (date: Date | undefined) => {
    if (date && onSelect) {
      onSelect(date)
    }
  }

  return (
    <div className={c(styles.calendar, className)} onScroll={onScroll}>
      <DayPicker mode="single" selected={selectedDate} onSelect={handleSingleSelect} {...commonProps} />
    </div>
  )
}

export default React.memo(Calendar)
